// fetchAttachment.js
import { fetchBinaryBlob } from "./fetchBinaryBlob"; // adjust path as needed

// 🔔 Replace this with your actual modal function
function showErrorModal(message) {
  console.error("Attachment Error:", message);
  // Example:
  // store.dispatch(openErrorModal(message));
}

/**
 * Extract pure base64 from possible data URLs.
 */
function extractBase64(data) {
  if (!data) {
    console.log("extractBase64: no data provided");
    return null;
  }
  const match = data.match(/^data:.*;base64,(.*)$/);
  if (match) {
    console.log("extractBase64: extracted base64 from data URL");
    return match[1];
  }
  console.log("extractBase64: using raw base64 string");
  return data;
}

/**
 * Decode raw or data-URL wrapped Base64 → Blob
 */
function decodeBase64ToBlob(base64, contentType = "application/octet-stream") {
  try {
    const clean = extractBase64(base64);
    if (!clean) {
      console.warn("decodeBase64ToBlob: no valid base64 to decode");
      return null;
    }
    const byteCharacters = atob(clean);
    const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
    console.log("decodeBase64ToBlob: blob created", blob);
    return blob;
  } catch (err) {
    console.error("decodeBase64ToBlob: failed", err);
    showErrorModal("Failed to decode base64 attachment.");
    return null;
  }
}

/**
 * Main function: Fetch an attachment, or decode base64, or pull from a FHIR resource.
 */
export const fetchAttachment = async (attachmentRef, fhirClient) => {
  console.log("----------------------fetchAttachment: starting fetch", attachmentRef);

  const { url, data, contentType, title } = attachmentRef || {};

  // ---------------------------------------------------------------------
  // 1️⃣ Local Base64 file (including data URLs)
  // ---------------------------------------------------------------------
  if (!url && data) {
    console.log("fetchAttachment: Detected local base64 file, decoding directly");

    const blob = decodeBase64ToBlob(data, contentType);
    if (!blob) {
      console.error("fetchAttachment: failed to create blob from base64");
      return { error: "Failed to decode attachment" };
    }

    return {
      type: "LocalFile",
      title: title || "file",
      contentType,
      blob
    };
  }

  // ---------------------------------------------------------------------
  // 2️⃣ External HTTP/HTTPS URL
  // ---------------------------------------------------------------------
  if (url?.startsWith("http")) {
    console.log("fetchAttachment: Detected external URL", url);
    return {
      type: "ExternalURL",
      title: title || url,
      contentType,
      url
    };
  }

  // ---------------------------------------------------------------------
  // 3️⃣ Missing URL entirely
  // ---------------------------------------------------------------------
  if (!url) {
    const msg = "No URL provided in attachment.";
    console.error("fetchAttachment:", msg, attachmentRef);
    showErrorModal(msg);
    return { error: msg };
  }

  // ---------------------------------------------------------------------
  // 4️⃣ FHIR server fetch (Binary, DocumentReference, etc.)
  // ---------------------------------------------------------------------
  console.log("fetchAttachment: fetching FHIR URL", url);

  try {
    const response = await fhirClient.request(url, {
      headers: { Accept: contentType || "*/*" }
    });

    // Raw fetch Response object (Binary blob)
    if (response instanceof Response) {
      console.log("fetchAttachment: received raw fetch Response");
      const blob = await response.blob();
      return {
        type: "Binary",
        title: title || url,
        contentType: blob.type || contentType,
        blob
      };
    }

    // Parsed FHIR Resource
    const resource = response;
    console.log("fetchAttachment: fetched resource type", resource?.resourceType, "id", resource?.id);

    switch (resource.resourceType) {

      // -----------------------
      // Binary Resource
      // -----------------------
      case "Binary": {
        if (resource.data) {
          const blob = decodeBase64ToBlob(resource.data, resource.contentType);
          if (!blob) {
            console.error("fetchAttachment: failed to decode Binary.data");
            return { error: "Failed to decode Binary resource" };
          }

          return {
            type: "Binary",
            id: resource.id,
            title: title || resource.id,
            contentType: resource.contentType,
            blob
          };
        }

        return {
          type: "Binary",
          id: resource.id,
          title: title || resource.id,
          url
        };
      }

      // -----------------------
      // DocumentReference
      // -----------------------
      case "DocumentReference": {
        const attachment =
          resource.content?.[0]?.attachment ||
          resource.attachment ||
          null;

        if (!attachment) {
          console.warn("fetchAttachment: DocumentReference has no attachment content");
        }

        const attData = attachment?.data || null;
        const attType = attachment?.contentType;
        const attUrl = attachment?.url || null;

        let blob = null;
        if (attData) {
          console.log("fetchAttachment: decoding DocumentReference attachment.data");
          blob = decodeBase64ToBlob(attData, attType);
          if (!blob) console.error("fetchAttachment: failed to decode attachment data");
        }

        return {
          type: "DocumentReference",
          id: `DocumentReference/${resource.id}`,
          resource,
          title: attachment?.title || resource.description || `DocumentReference/${resource.id}`,
          contentType: attType,
          url: attUrl,
          data: attData,
          blob
        };
      }

      // -----------------------
      // Observation / DiagnosticReport
      // -----------------------
      case "Observation":
      case "DiagnosticReport":
        return {
          type: resource.resourceType,
          id: `${resource.resourceType}/${resource.id}`,
          resource,
          title: resource.code?.text || `${resource.resourceType}/${resource.id}`
        };

      default:
        console.warn("fetchAttachment: unknown resource type", resource.resourceType);
        return {
          type: resource.resourceType || "Unknown",
          id: resource.id,
          resource
        };
    }
  } catch (err) {
    console.error("fetchAttachment: fetch failed for URL", url, err);

    const message = `Failed to fetch attachment: ${err.message}`;
    showErrorModal(message);

    return { error: message };
  }
};
